import {
  takeLatest,
  takeEvery,
  takeLeading,
  call,
  select,
  put,
  all,
  take,
  fork,
  delay,
  race,
} from "redux-saga/effects";
import * as actions from "../state/notifications/actions";
import * as documentActions from "../state/document/actions";
import * as notifyToasterContentActions from "../state/notify-toaster-content/actions";
import { selectedNationalCommittee } from "../state/national-committees/selectors";
import {
  updateNotification,
  deleteNotification,
  createNotification,
  getNotification,
  pinNotification,
  unpinNotification,
  markNotificationsAsSeen,
  markAllNotificationAsSeen,
  waitForNotificationId,
  sendInstantEmailWithNotificationIdUrl,
  sendInstantEmailWithCommitteeIdUrl,
  updatePollUrl,
  voteNotificationPollUrl,
  deleteVoteNotificationPollUrl,
  createPollUrl,
} from "../api/resources/notifications";
import i18next from "i18next";

import { isRequestSuccessful } from "../api/utils";

import history from "../utils/history";
import { transformCommitteeName } from "../utils/transform";
import {
  DIALOG_TYPES,
  PAGE_TYPES,
  generateDialogLink,
  generateDialogLinkForPage,
} from "../utils/dynamicLink";

import * as selectors from "../state/notifications/selectors";
import { getNewNotificationFormData } from "../state/notifications/selectors";
import { mapToApiModel } from "../api/mappers/notification";
import { addGeneralNotificationInformation } from "../state/notifications/actions";
import { getDocumentUrl } from "../api/resources/document";
import { safeReferenceRequest } from "../api/safeReferenceRequest";
import { initDocuments } from "../state/document/actions";

import * as userSelectors from "../state/user/selectors";
import { decreaseNumberOfUnseenNotifications } from "../state/user/actions";
import { documentFilters } from "../utils/documentUtils";
import { getMeetingUrl } from "../api/resources/meetings";
import { getDeadlineUrl } from "../api/resources/deadlines";
import { referenceListToReferenceDataPerType } from "../utils/references";
import { normalizeObjectOfReferences } from "../state/references/normalizers";
import { removeReferenceById } from "../state/document/normalizers";
import { NOTIFY_TOASTER_TYPE } from "../state/notify-toaster-content/reducer";
import { INSTANT_EMAIL_PARAM_TYPE } from "../state/notifications/reducer";
import { notificationType } from "../utils/constants";
import { isObjectEmpty } from "../utils/validation/objectValidator";

function* handleUpdateNotification() {
  try {
    const oldNotification = yield select(selectors.getNotification);
    if (!oldNotification) throw new Error("Notification not found");

    const notification = yield select(getNewNotificationFormData);
    const poll =
      !notification.poll || isObjectEmpty(notification.poll)
        ? null
        : {
            ...notification.poll,
            notificationUri: notification.uri,
            options: notification.poll?.options.map((option) => ({
              ...option,
              pollNotificationUri: notification.uri,
            })),
            deadline: notification.poll.deadline ?? null,
          };

    if (poll) {
      // These used to abort the save by throwing; they now catch so they are
      // safe as root watchers, so the abort has to come from their result.
      // Without it a failed poll save would fall through, store the
      // notification and navigate away as if everything had saved.
      // isObjectEmpty, not raw truthiness: the API hands back poll: {} for a
      // notification with no poll, which is what line 75 above and
      // NotificationDetails both test for. Taking the update branch for {}
      // PATCHes a poll that does not exist, and the abort below then blocks the
      // whole save, so a poll could never be added to such a notification.
      const pollSaved =
        oldNotification.poll && !isObjectEmpty(oldNotification.poll)
          ? yield call(handleUpdatePoll, { payload: poll })
          : yield call(handleCreatePoll, { payload: poll });

      if (!pollSaved) throw new Error("poll save failed");
    }
    const response = yield call(updateNotification, notification);

    if (response.status === 200) {
      yield call(waitForNotificationId, notification.uri);
      yield put(actions.updateNotificationSucceeded(response.data));
      history.push(
        generateDialogLink(DIALOG_TYPES.NOTIFICATION_DETAILS, notification.uri),
      );
      yield put(actions.resetNotificationsFilters());
      yield put(actions.initNotifications());
    } else {
      yield put(
        actions.updateNotificationFailed("Failed to update notification"),
      );
    }
  } catch (error) {
    console.error(`update notification failed - ${error}`);
    // isProcessingRequest is set by updateNotification/createNotification/
    // deleteNotification and cleared only by their success and failure actions.
    // The interceptor rejects on every non-2xx, so the catch IS the failure
    // path: without a dispatch here the notifications list spinner stays on
    // until an unrelated action (a list page-in, say) happens to clear it.
    // handleCreateNotification and handleDeletingNotification do the same.
    yield put(actions.updateNotificationFailed(`${error}`));
  }
}

function* handleDeletingNotification(action) {
  try {
    const committeeId = yield select(selectedNationalCommittee);
    const notificationId = action.payload;

    const response = yield call(deleteNotification, notificationId);

    if (isRequestSuccessful(response)) {
      yield put(actions.deleteNotificationSucceeded(notificationId));
      history.push(`/${transformCommitteeName(committeeId)}/notifications`);
    } else {
      yield put(
        actions.deleteNotificationFailed("Failed to delete notification"),
      );
    }
  } catch (error) {
    console.error(`delete notification failed - ${error}`);
    yield put(actions.deleteNotificationFailed(`${error}`));
  }
}

export function* handleCreateNotificationBySystem(data) {
  yield call(handleCreateNotification, data, true);
}

export function* handleCreateNotification(data, isBySystem = false) {
  try {
    const committeeId = yield select(selectedNationalCommittee);
    const notificationData = isBySystem
      ? data.payload
      : yield select(selectors.getNewNotificationFormData);
    const memberId = yield select(userSelectors.userId);

    const response = yield call(
      createNotification,
      committeeId,
      mapToApiModel(notificationData, memberId, isBySystem, committeeId),
    );
    if (response.status === 200) {
      yield put(actions.createNotificationSucceeded(response.data));
      yield call(waitForNotificationId, response.data.uri);

      if (!isBySystem) {
        history.push(`/${transformCommitteeName(committeeId)}/notifications`);
        yield put(actions.resetNotificationsFilters());
        yield put(actions.initNotifications());
      }
    } else {
      yield put(
        actions.createNotificationFailed("Failed to create notification"),
      );
    }
  } catch (error) {
    console.error(`create notification failed - ${error}`);
    // Only on the user-initiated path. handleCreateNotificationBySystem calls
    // this directly from the votes, meetings, deadlines, comments and document
    // sagas without dispatching createNotification, so isProcessingRequest was
    // never set by this flow - and createNotificationFailed clears it, which
    // would end an unrelated notifications-list load early and render it empty.
    if (!isBySystem) {
      yield put(actions.createNotificationFailed(`${error}`));
    }
  }
}

function* handleInitNotificationForm(action) {
  try {
    yield put(initDocuments());

    const notificationId = action.payload;
    const notification = yield call(getNotification, notificationId);
    yield put(actions.fetchNotificationSucceeded(notification.data));

    if (notification.status === 200) {
      yield put(
        addGeneralNotificationInformation({
          uri: notification.data.uri,
          title: notification.data.title,
          description: notification.data.description,
          notifyOnUpdate: notification.data.notifyOnUpdate,
          referencesRawResponsePerType: referenceListToReferenceDataPerType(
            notification.data.references,
          ),
          poll: notification.data.poll,
        }),
      );

      yield call(handleFetchNotificationReferences, REFERENCE_TARGET.FORM);

      // let documents = yield all(
      //   notification.data.references.map((document) =>
      //     call(getDocumentSafe, document.uri),
      //   ),
      // );
      // documents = documents.filter((d) => d !== null && d.status === 200);

      // if (documents) {
      //   yield put(initNotificationFormWithDocuments(documents));
      // }
    }
  } catch (error) {
    console.error(`init notification form failed - ${error}`);
  }
}

function* handleCreateNotificationFromDocument(action) {
  try {
    const selectedDocument = action.payload;
    yield put(actions.setSelectedNotificationDocuments([selectedDocument]));
    yield call(createNotificationFromReferences, selectedDocument);
  } catch (error) {
    console.error(
      `create notification from selected documents failed - ${error}`,
    );
  }
}

function* createNotificationFromReferences(reference) {
  yield put(
    actions.addGeneralNotificationInformation({
      title: reference.title,
    }),
  );
  const committeeId = yield select(selectedNationalCommittee);
  history.push(
    generateDialogLinkForPage(
      committeeId,
      PAGE_TYPES.NOTIFICATION,
      DIALOG_TYPES.CREATE_NOTIFICATION,
    ),
  );
}

function* handleCreateNotificationFromMeeting(action) {
  try {
    const selectedMeeting = action.payload;
    yield put(actions.setSelectedNotificationMeetings([selectedMeeting]));
    yield call(createNotificationFromReferences, selectedMeeting);
  } catch (error) {
    console.error(
      `create notification from selected meeting failed - ${error}`,
    );
  }
}

function* handleCreateNotificationFromDeadline(action) {
  try {
    const selectedDeadline = action.payload;
    yield put(actions.setSelectedNotificationDeadlines([selectedDeadline]));
    yield call(createNotificationFromReferences, selectedDeadline);
  } catch (error) {
    console.error(
      `create notification from selected deadline failed - ${error}`,
    );
  }
}

function* handleDownloadNotificationDocuments(action) {
  try {
    const notificationId = action.payload;
    const documentsUris = yield select(
      selectors.getNotificationDocumentsUri,
      notificationId,
    );
    const notificationUri = yield select(
      selectors.getNotificationUri,
      notificationId,
    );

    yield put(
      documentActions.downloadZipPackage({
        id: notificationId,
        packageName: `${notificationUri || "documents"}.zip`,
        uris: documentsUris,
      }),
    );
  } catch (error) {
    console.error(`download notification documents failed - ${error}`);
  }
}

function* handleFetchUpdatedNotificationDocument(action) {
  try {
    const url = action.payload;

    yield take(documentActions.editDocumentSucceeded);
    const document = yield call(getDocumentUrl, url);
    if (document.status === 200) {
      yield put(
        actions.fetchUpdatedNotificationDocumentSucceeded(document.data),
      );
    } else {
      yield put(
        actions.fetchUpdatedNotificationDocumentFailed(
          "Failed to fetch updated notification",
        ),
      );
    }
  } catch (error) {
    console.error(`fetch updated notification documents failed - ${error}`);
  }
}

function* handleRemoveReferenceFromNotification(action) {
  try {
    const notification = yield select(selectors.getNotification);
    // meeting.references = meeting.references.filter(doc => doc.url !== action.payload);

    // for(const key in notification.referencesPerType) {
    //   notification.referencesPerType[key] =
    //  notification.referencesPerType[key].filter((doc) => doc.uri !== action.payload);
    // }
    notification.referencesPerType = removeReferenceById(
      notification.referencesPerType,
      action.payload,
    );

    const response = yield call(updateNotification, notification);

    if (response.status === 200) {
      // TODO: Do we need this?
      yield call(waitForNotificationId, notification.uri);
      yield put(
        actions.removeReferenceFromNotificationSucceeded(response.data),
      );
      yield call(
        handleFetchNotificationReferences,
        REFERENCE_TARGET.NOTIFICATION,
      );
    } else {
      yield put(
        actions.updateNotificationFailed("Failed to update notification"),
      );
    }
    // const response = yield call(updateMeetingUrl, notification, false);

    // if (response.status === 200) {
    //     yield put(actions.fetchCurrentMeetingDocuments());
    //     yield put(actions.updateMeetingSucceeded(response.data));
    // } else {
    //     yield put(actions.updateMeetingFailed("Failed to update meeting"));
    // }
  } catch (error) {
    yield put(
      actions.updateNotificationFailed(
        "Failed to update Notification References",
      ),
    );
    console.error(`handleRemoveReferenceFromNotification failed - ${error}`);
  }
}

// Returns whether the notification was actually refreshed. Callers that gate UI
// on the refresh (the poll vote saga) cannot otherwise tell a silent failure
// from a success, and would release their lock against stale data.
function* handleFetchNotification(action) {
  try {
    const uri = action.payload;
    const notificationResponse = yield call(getNotification, uri);

    if (!isRequestSuccessful(notificationResponse)) return false;

    yield put(actions.fetchNotificationSucceeded(notificationResponse.data));

    yield call(
      handleFetchNotificationReferences,
      REFERENCE_TARGET.NOTIFICATION,
    );

    return true;
  } catch (error) {
    console.error(`getting notification details failed - ${error}`);
    return false;
  }
}

const REFERENCE_TARGET = {
  FORM: "form",
  NOTIFICATION: "notification",
};
// target: REFERENCE_TARGET
function* handleFetchNotificationReferences(target) {
  try {
    const isNotification = target === REFERENCE_TARGET.NOTIFICATION;

    // yield take(actions.fetchNotificationSucceeded);
    const notification = yield select(
      isNotification
        ? selectors.getNotification
        : selectors.getNewNotificationFormData,
    );

    const documentTargets =
      notification.referencesRawResponsePerType.document ?? [];
    const meetingTargets =
      notification.referencesRawResponsePerType.meeting ?? [];
    const deadlineTargets =
      notification.referencesRawResponsePerType.deadline ?? [];
    const documentsResponse = yield all(
      documentTargets.map((element) =>
        call(() =>
          safeReferenceRequest(
            getDocumentUrl(element.uri, { tolerate401: true }),
            { uri: element.uri },
          ),
        ),
      ),
    );
    const document = documentFilters.filterEmptyDocuments(documentsResponse);

    const meetingsResponse = yield all(
      meetingTargets.map((element) =>
        call(() =>
          safeReferenceRequest(
            getMeetingUrl(element.uri, { tolerate401: true }),
            { uri: element.uri },
          ),
        ),
      ),
    );
    const meeting = documentFilters.filterEmptyDocuments(meetingsResponse);

    const deadlinesResponse = yield all(
      deadlineTargets.map((element) =>
        call(() =>
          safeReferenceRequest(
            getDeadlineUrl(element.uri, { tolerate401: true }),
            { uri: element.uri },
          ),
        ),
      ),
    );
    const deadline = documentFilters.filterEmptyDocuments(deadlinesResponse);

    const referencesPerType = normalizeObjectOfReferences({
      document,
      meeting,
      deadline,
    });

    const nextAction = isNotification
      ? actions.fetchNotificationReferencesSucceeded
      : actions.addGeneralNotificationInformation;

    // The uri rides along so the reducer can drop a result that arrives after
    // the user moved on: this fan-out is one request per reference and can
    // outlast the notification it was started for.
    yield put(
      nextAction(
        isNotification
          ? { referencesPerType, uri: notification.uri }
          : { referencesPerType },
      ),
    );

    // Explicit, because the poll vote resync races this and reads the result:
    // with no return it resolved undefined on success as well as on failure,
    // which read as "not ok" and showed a red reload-the-page toast after every
    // vote that worked. A lost race also yields undefined, which still reads as
    // not ok, so only this true means the fan-out finished.
    return true;
  } catch (error) {
    console.error(`fetch deadline references failed - ${error}`);
    return false;
  }
}

function* handlePinNotification(action) {
  try {
    const notificationId = action.payload;

    const response = yield call(pinNotification, notificationId);

    if (isRequestSuccessful(response)) {
      yield put(actions.pinNotificationSucceeded(notificationId));
    }
  } catch (error) {
    console.error(error);
  }
}

function* handleUnpinNotification(action) {
  try {
    const notificationId = action.payload;

    const response = yield call(unpinNotification, notificationId);

    if (isRequestSuccessful(response)) {
      yield put(actions.unpinNotificationSucceeded(notificationId));
    }
  } catch (error) {
    console.error(error);
  }
}

function* handleMarkNotificationAsSeen(notification) {
  try {
    const response = yield call(markNotificationsAsSeen, [notification]);
    if (isRequestSuccessful(response)) {
      const committeeId = yield select(selectedNationalCommittee);
      yield put(actions.markNotificationsAsSeenSucceeded(notification));
      yield put(decreaseNumberOfUnseenNotifications(committeeId));
    }
  } catch (error) {
    console.error(error);
  }
}

function* handleMarkNotificationsAsSeen(action) {
  const notifications = action.payload;

  for (let item of notifications) {
    yield fork(handleMarkNotificationAsSeen, item);
  }
}

function* handleMarkAllNotificationsAsSeen() {
  try {
    const committeeId = yield select(selectedNationalCommittee);

    var response = yield call(markAllNotificationAsSeen, committeeId);

    //The operation is to fast, and the loader animation just blinks.
    //adding a small delay will make it feel better
    yield delay(500);
    if (isRequestSuccessful(response)) {
      yield put(actions.markAllNotificationsAsSeenSucceeded(committeeId));
    } else {
      yield put(actions.markAllNotificationsAsSeenFailed());
      console.error("error while marking all notification as seen");
    }
  } catch (error) {
    console.error(error);
    yield put(actions.markAllNotificationsAsSeenFailed());
  }
}

// Exported for tests. A 401 on a single document (user lost access to it)
// becomes a forbidden stub instead of bubbling up and aborting the whole
// notifications page pull.
export function* fetchDocuments(documents) {
  const documentsResponse = yield all(
    documents.map((document) =>
      call(() =>
        safeReferenceRequest(
          getDocumentUrl(document.uri, { tolerate401: true }),
          { uri: document.uri },
        ),
      ),
    ),
  );
  return documentFilters
    .filterEmptyDocuments(documentsResponse)
    .map((d) => d.data);
}

function* handlePullNextNotificationsPage() {
  try {
    const committeeId = yield select(selectedNationalCommittee);
    const filters = yield select(selectors.getNotificationsFilters);
    const repository = yield select(selectors.getNotificationRepository);

    const pagination = yield select(selectors.getPagination);
    const pageToLoad = pagination.pagesLoaded + 1;

    const response = yield repository.pullUserNotificationsPage(
      committeeId,
      filters,
      filters.onlyUnseen ? 1 : pageToLoad,
    );

    const pagesToLoad = Math.ceil(
      response.data.total / response.data.page_size,
    );

    let hasMoreToLoad =
      response.data.results.length < response.data.total &&
      response.data.results &&
      response.data.results.length > 0 &&
      response.data.results.length + pageToLoad * response.data.page_size <
        response.data.total;

    if (!isRequestSuccessful(response)) {
      yield put(actions.pullNotificationsPageFailed());
      console.error(response);
      console.error(
        `Failed to pull notifications page ${response.data.message}`,
      );
      return;
    }

    if (filters.onlyUnseen && response.data.results.length > 0) {
      yield call(
        markNotificationsAsSeen,
        response.data.results.map((n) => n.uri),
      );
    }

    let exportedActions = [];
    const onActionExport = (action) => {
      exportedActions = [...exportedActions, action];
    };

    const details = yield all(
      response.data.results.map((r) =>
        repository.pullNotificationDetails(r, onActionExport),
      ),
    );

    let successfulDetails = details.filter((d) => !!d && !d.failed);
    const failedDetails = details.filter((d) => !!d && d.failed);

    yield all(exportedActions.map((a) => put(a)));

    yield put(
      actions.pullNotificationsPageSucceeded({
        ...response.data,
        pagesToLoad,
        hasMoreToLoad: hasMoreToLoad,
      }),
    );

    const notifications = yield select(selectors.notifications);

    /*
      When notification.type === notificationType.internalDocumentSummation, 
      fetch document details to show it to the notifications.
    */
    const successfulDetailsWithFetchedDocuments = yield all(
      successfulDetails.map(function* (d) {
        if (
          notifications.data[d.id]?.type ===
          notificationType.internalDocumentSummation
        ) {
          d.documents = yield call(fetchDocuments, d.documents);
        }
        return d;
      }),
    );

    yield all(
      successfulDetailsWithFetchedDocuments.map((d) =>
        put(actions.getNotificationDetailsSucceeded(d)),
      ),
    );
    yield all(
      failedDetails.map((d) => put(actions.getNotificationDetailsFailed(d))),
    );
  } catch (error) {
    console.error(error);
    console.error(`Failed to pull notifications page ${error}`);
  }
}

function* initNotifications() {
  try {
    const query = new URLSearchParams(history.location.search);
    yield put(actions.pullNextNotificationsPage());
  } catch (error) {
    console.error(`init notifications filters failed - ${error}`);
  }
}

export function* handleSendInstantEmail(action) {
  const { type, param, disableToast } = action.payload;

  const url =
    type === INSTANT_EMAIL_PARAM_TYPE.NOTIFICATION_ID
      ? sendInstantEmailWithNotificationIdUrl
      : sendInstantEmailWithCommitteeIdUrl;

  let notifyToasterContent = {
    title: "Email failed",
    message: i18next.t("notifyToaster.error", { ns: "common" }),
    type: NOTIFY_TOASTER_TYPE.ERROR,
  };

  // Same root-saga hazard as handleUpdatePoll: a rejected send used to escape
  // the takeEvery and take every notification watcher down with it.
  try {
    const response = yield call(url, param);
    if (!isRequestSuccessful(response)) throw new Error("send email failed");

    yield put(actions.sendInstantEmailSucceeded(param));

    notifyToasterContent = {
      title: "Email sent",
      message: i18next.t("notifyToaster.success", { ns: "common" }),
      type: NOTIFY_TOASTER_TYPE.SUCCESS,
    };
  } catch (error) {
    console.error(`send instant email failed - ${error}`);
  }

  if (disableToast) return;

  yield put(
    notifyToasterContentActions.setNotifyToasterContent(notifyToasterContent),
  );
}

// The vote saga cannot reuse handleFetchNotification for its resync: that puts
// fetchNotificationSucceeded as soon as the payload arrives, and the reducer
// replaces state.notifications.notification wholesale, so a user who opened a
// different notification while the vote was in flight gets this one's content
// rendered under it. Checking the open uri before the request left a window as
// wide as the request; this re-checks with no awaited I/O before the put.
//
// Both phases are raced because api.js configures no axios timeout, and the
// reference phase fans out one request per referenced document, meeting and
// deadline. An unbounded hang here would never release pollVoteInProgress and
// takeLeading would never re-arm, leaving the poll dead until a reload.
//
// Returns { ok, timedOut }. The caller needs timedOut separately because the
// toaster holds one slot and clears itself after ~3.5s: a resync that fails in
// the same tick as the vote would replace the vote's message, while one that
// times out lands 15s later, long after that message has gone.
function* resyncNotificationAfterVote(notificationUri) {
  try {
    return yield call(runResync, notificationUri);
  } catch (error) {
    // The interceptor rejects on every non-2xx, so this is the ordinary
    // failure path, not an edge case. handleFetchNotification used to absorb
    // it; going direct means absorbing it here, or the caller cannot tell a
    // failed resync from a good one and unlocks against stale votes.
    console.error(`poll vote resync failed - ${error}`);
    return { ok: false, timedOut: false };
  }
}

function* runResync(notificationUri) {
  const { response, timeout } = yield race({
    response: call(getNotification, notificationUri),
    timeout: delay(RESYNC_TIMEOUT_MS),
  });

  if (timeout) return { ok: false, timedOut: true };
  if (!response || !isRequestSuccessful(response)) {
    return { ok: false, timedOut: false };
  }

  const stillOpen = yield select(selectors.getNotificationUri);
  if (stillOpen !== notificationUri) return { ok: true, timedOut: false };

  yield put(actions.fetchNotificationSucceeded(response.data));

  // fetchNotificationSucceeded drops referencesPerType, so if this phase does
  // not finish the details page silently loses its attachments.
  const { references, timeout: referencesTimedOut } = yield race({
    references: call(
      handleFetchNotificationReferences,
      REFERENCE_TARGET.NOTIFICATION,
    ),
    timeout: delay(RESYNC_TIMEOUT_MS),
  });

  // === true, not !== undefined: the fan-out returns false when it fails and
  // undefined only when it loses the race, so anything but true is not ok.
  return {
    ok: references === true,
    timedOut: Boolean(referencesTimedOut),
  };
}

// Long enough not to trip on a slow-but-working resync, short enough that a
// hung one does not read as a broken poll.
const RESYNC_TIMEOUT_MS = 15000;

// One action handles the whole change: removing a vote, casting one, or
// swapping in a single-choice poll. Removal runs BEFORE the new vote so the
// backend is never holding two votes at once, and the notification is refetched
// exactly once at the end instead of one racing refetch per request.
// The trade-off of that ordering: if the removal commits and the new vote then
// fails, the user is left with no vote. The toast says so, because the
// alternative order would corrupt the tally with two votes instead.
export function* handleVoteNotificationPoll(action) {
  const { pollOptionId, pollVoteId } = action.payload;
  let notificationUri;
  let failure = null;
  let removedExistingVote = false;

  try {
    notificationUri = yield select(selectors.getNotificationUri);

    if (pollVoteId) {
      const removed = yield call(
        deleteVoteNotificationPollUrl,
        pollVoteId,
        notificationUri,
      );
      if (!isRequestSuccessful(removed)) throw new Error("remove vote failed");
      removedExistingVote = true;
    }

    if (pollOptionId) {
      const userId = yield select(userSelectors.userId);
      const voted = yield call(
        voteNotificationPollUrl,
        notificationUri,
        userId,
        pollOptionId,
      );
      if (!isRequestSuccessful(voted)) throw new Error("cast vote failed");
    }
  } catch (error) {
    failure = `${error}`;
    console.error(`poll vote failed - ${error}`);
    // NotifyToaster renders `message` only - its title h2 is commented out - and
    // notifyToaster.error reads "Failed to send email", so the meaning has to
    // be in `message` and cannot reuse that key.
    yield put(
      notifyToasterContentActions.setNotifyToasterContent({
        title: "Vote failed",
        message: i18next.t(
          removedExistingVote && pollOptionId
            ? "notifyToaster.voteReplaceFailed"
            : "notifyToaster.voteFailed",
          { ns: "common" },
        ),
        type: NOTIFY_TOASTER_TYPE.ERROR,
      }),
    );
  } finally {
    // Nothing in here may throw: an escaping error from a finally block wins
    // over everything, so it would skip the release below, leave
    // pollVoteInProgress stuck true and take the notificationsSaga root down
    // with it. The release is the last statement and sits outside this try.
    try {
      if (notificationUri) {
        // Resync either way, so a failed vote can't leave the bars showing a
        // vote that was never stored. It has to finish before the release below:
        // the component derives which vote to remove from these votes, so
        // re-enabling the checkboxes against stale data would let the next click
        // send the wrong pollVoteId.
        const { ok, timedOut } = yield call(
          resyncNotificationAfterVote,
          notificationUri,
        );

        // Says only that the view is stale, never anything about the vote: it
        // fires on a successful vote, a failed one and a pure removal alike.
        // Suppressed when the vote already failed in this same tick, because
        // the toaster holds one slot and would drop the message that matters -
        // but not on a timeout, which lands 15s later, long after the toaster
        // cleared that message.
        if (!ok && (timedOut || !failure)) {
          yield put(
            notifyToasterContentActions.setNotifyToasterContent({
              title: "Poll out of date",
              message: i18next.t("notifyToaster.pollOutOfDate", {
                ns: "common",
              }),
              type: NOTIFY_TOASTER_TYPE.ERROR,
            }),
          );
        }
      }
    } catch (error) {
      console.error(`poll vote resync failed - ${error}`);
    }

    yield put(
      failure
        ? actions.voteNotificationPollFailed(failure)
        : actions.voteNotificationPollSucceeded(),
    );
  }
}

// updatePollFailed/createPollFailed have no reducer handler, so the toast is
// the only thing that reaches the user.
function* notifyPollSaveFailed() {
  yield put(
    notifyToasterContentActions.setNotifyToasterContent({
      title: "Poll not saved",
      message: i18next.t("notifyToaster.pollNotSaved", { ns: "common" }),
      type: NOTIFY_TOASTER_TYPE.ERROR,
    }),
  );
}

// api.js sets no validateStatus and its interceptor rejects on every error
// path, so a non-2xx REJECTS rather than returning - reading response.status
// without a catch means a 500 escapes the takeEvery below and tears down the
// whole notificationsSaga root, disabling every notification feature until a
// reload. Reached both from handleUpdateNotification (which has its own catch)
// and directly from the root watcher (which does not). Returns whether it
// saved, because handleUpdateNotification needs that to abort.
export function* handleUpdatePoll(action) {
  try {
    const notificationUri = yield select(selectors.getNotificationUri);
    const response = yield call(updatePollUrl, action.payload, notificationUri);

    if (!isRequestSuccessful(response)) throw new Error("update poll failed");

    yield put(actions.updatePollSucceeded(response.data));
    return true;
  } catch (error) {
    console.error(`update poll failed - ${error}`);
    yield put(actions.updatePollFailed("Failed to update poll"));
    yield call(notifyPollSaveFailed);
    return false;
  }
}

export function* handleCreatePoll(action) {
  try {
    const committeeId = yield select(selectedNationalCommittee);
    const response = yield call(createPollUrl, action.payload, committeeId);

    if (!isRequestSuccessful(response)) throw new Error("create poll failed");

    yield put(actions.createPollSucceeded(response.data));
    return true;
  } catch (error) {
    console.error(`create poll failed - ${error}`);
    yield put(actions.createPollFailed("Failed to create poll"));
    yield call(notifyPollSaveFailed);
    return false;
  }
}

export default function* notificationsSaga() {
  yield takeLatest(actions.fetchNotification, handleFetchNotification);
  yield takeLatest(actions.initNotifications, initNotifications);
  yield takeLatest(
    actions.fetchNotificationReferences,
    handleFetchNotificationReferences,
  );

  yield takeLeading(
    actions.pullNextNotificationsPage,
    handlePullNextNotificationsPage,
  );

  yield takeLatest(actions.updateNotification, handleUpdateNotification);
  yield takeLatest(actions.deleteNotification, handleDeletingNotification);
  yield takeLatest(actions.createNotification, handleCreateNotification);
  yield takeLatest(
    actions.initNotificationFormWithGeneralInfo,
    handleInitNotificationForm,
  );
  yield takeLatest(
    actions.createNotificationFromDocument,
    handleCreateNotificationFromDocument,
  );
  yield takeLatest(
    actions.createNotificationFromMeeting,
    handleCreateNotificationFromMeeting,
  );
  yield takeLatest(
    actions.createNotificationFromDeadline,
    handleCreateNotificationFromDeadline,
  );
  yield takeLatest(
    actions.fetchUpdatedNotificationDocument,
    handleFetchUpdatedNotificationDocument,
  );
  yield takeEvery(
    actions.removeReferenceFromNotification,
    handleRemoveReferenceFromNotification,
  );
  yield takeEvery(
    actions.downloadNotificationDocuments,
    handleDownloadNotificationDocuments,
  );

  yield takeEvery(actions.pinNotification, handlePinNotification);
  yield takeEvery(actions.unpinNotification, handleUnpinNotification);
  yield takeEvery(
    actions.markNotificationsAsSeen,
    handleMarkNotificationsAsSeen,
  );
  yield takeEvery(
    actions.markAllNotificationsAsSeen,
    handleMarkAllNotificationsAsSeen,
  );
  yield takeEvery(actions.sendInstantEmail, handleSendInstantEmail);
  // takeLeading, not takeEvery: extra clicks landing while a vote is in flight
  // are dropped instead of queued into duplicate votes.
  yield takeLeading(actions.voteNotificationPoll, handleVoteNotificationPoll);
  yield takeEvery(actions.updatePoll, handleUpdatePoll);
}
